import {
  useCallback,
  useMemo,
  useEffect,
  useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import Autocomplete from '../components/SearchableAutocomplete';
import TextField from '../components/SearchableTextField';
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import SidebarLayout from "../components/sidebarLayout";
import { useTranslation } from "react-i18next";
import { italianErrorMessage } from "../utils/italianErrorMessage";
import { getAllClienti, getAllCompanies } from "../APIs/ClientsAPI";
import { getAllProdotti } from "../APIs/ProductsAPI";
import { getAllPackages } from "../APIs/PackagesAPI";
import { getAllTavoli } from "../APIs/TablesAPI";
import { inviaPreventivoEmail } from "../APIs/PreventiviAPI";
import { createReservationEvent } from "../APIs/ReservationEventsAPI";
import { getTenantSettings } from "../APIs/TenantSettingsAPI";
import DateTimeLocal24Field from "../components/DateTimeLocal24Field";

function money(value) {
  return Number(value || 0).toFixed(2);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

const EMAIL_COPY = {
  it: {
    title: "Preventivo Offerta",
    confirmationTitle: "Conferma prenotazione",
    greeting: "Gentile",
    defaultMessage: "La ringraziamo per il suo interesse. Di seguito trova il preventivo dettagliato per i servizi richiesti. Siamo a disposizione per qualsiasi chiarimento o modifica.",
    confirmationDefaultMessage: "Confermiamo i dettagli della prenotazione riportati di seguito. Restiamo a disposizione per eventuali richieste o modifiche.",
    eventDate: "Data evento",
    rooms: "Sale impegnate",
    setup: "Allestimento",
    usageType: "Tipo di utilizzo",
    validUntil: "Questo preventivo è valido fino al",
    service: "Servizio",
    quantity: "Quantità",
    quantityUnit: "Quantità / Unità",
    unitPrice: "Prezzo unit.",
    total: "Totale",
    overallTotal: "Totale complessivo",
    contactLine: "Per accettare il preventivo o richiedere modifiche, non esiti a contattarci.",
    assistanceLine: "Saremo lieti di assisterla nella pianificazione del suo evento.",
    confirmationContactLine: "La prenotazione è stata registrata con i dettagli indicati.",
    confirmationAssistanceLine: "Per eventuali variazioni, risponda a questa e-mail.",
    footer: (brand) => `Questo preventivo è stato generato automaticamente dal sistema di gestione ${brand}.`,
    confirmationFooter: (brand) => `Questa conferma di prenotazione è stata generata dal sistema di gestione ${brand}.`,
  },
  en: {
    title: "Quote Offer",
    confirmationTitle: "Reservation confirmation",
    greeting: "Dear",
    defaultMessage: "Thank you for your interest. Below you will find the detailed quote for the requested services. We remain available for any clarification or changes.",
    confirmationDefaultMessage: "We confirm the reservation details shown below. We remain available for any requests or changes.",
    eventDate: "Event date",
    rooms: "Booked rooms",
    setup: "Setup",
    usageType: "Usage type",
    validUntil: "This quote is valid until",
    service: "Service",
    quantity: "Quantity",
    quantityUnit: "Qty / Unit",
    unitPrice: "Unit price",
    total: "Total",
    overallTotal: "Overall total",
    contactLine: "To accept the quote or request changes, please contact us.",
    assistanceLine: "We will be happy to assist you in planning your event.",
    confirmationContactLine: "The reservation has been recorded with the details shown above.",
    confirmationAssistanceLine: "For any changes, please reply to this email.",
    footer: (brand) => `This quote was generated automatically by the ${brand} management system.`,
    confirmationFooter: (brand) => `This reservation confirmation was generated by the ${brand} management system.`,
  },
  sq: {
    title: "Ofertë",
    confirmationTitle: "Konfirmimi i rezervimit",
    greeting: "I nderuar",
    defaultMessage: "Ju falënderojmë për interesin. Më poshtë gjeni ofertën e detajuar për shërbimet e kërkuara. Jemi në dispozicion për çdo sqarim ose ndryshim.",
    confirmationDefaultMessage: "Konfirmojmë detajet e rezervimit të paraqitura më poshtë. Jemi në dispozicion për çdo kërkesë ose ndryshim.",
    eventDate: "Data e eventit",
    rooms: "Sallat e rezervuara",
    setup: "Konfigurimi",
    usageType: "Lloji i perdorimit",
    validUntil: "Kjo ofertë është e vlefshme deri më",
    service: "Shërbimi",
    quantity: "Sasia",
    quantityUnit: "Sasia / Njësia",
    unitPrice: "Çmimi për njësi",
    total: "Totali",
    overallTotal: "Totali i përgjithshëm",
    contactLine: "Për të pranuar ofertën ose për të kërkuar ndryshime, ju lutemi na kontaktoni.",
    assistanceLine: "Do të jemi të lumtur t’ju ndihmojmë në planifikimin e eventit tuaj.",
    confirmationContactLine: "Rezervimi është regjistruar me detajet e paraqitura më sipër.",
    confirmationAssistanceLine: "Për ndryshime, ju lutemi përgjigjuni këtij email-i.",
    footer: (brand) => `Kjo ofertë u gjenerua automatikisht nga sistemi i menaxhimit ${brand}.`,
    confirmationFooter: (brand) => `Ky konfirmim rezervimi u gjenerua nga sistemi i menaxhimit ${brand}.`,
  },
};

function normalizeEmailLanguage(language) {
  const code = String(language || "it").toLowerCase().split(/[-_]/)[0];
  return EMAIL_COPY[code] ? code : "it";
}

function formatDatePart(datePart, language) {
  const match = String(datePart || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return datePart;
  return language === "en"
    ? `${match[2]}/${match[3]}/${match[1]}`
    : `${match[3]}/${match[2]}/${match[1]}`;
}

function formatEmailDate(value, language) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  return raw.split(",").map((part) => {
    const normalized = part.trim();
    const rangeMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s]+(\d{2}:\d{2})(?:\s*-\s*(\d{2}:\d{2}))?)?$/);
    if (rangeMatch) {
      const date = formatDatePart(rangeMatch[1], language);
      if (rangeMatch[2] && rangeMatch[3]) return `${date} ${rangeMatch[2]}-${rangeMatch[3]}`;
      if (rangeMatch[2]) return `${date} ${rangeMatch[2]}`;
      return date;
    }

    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return normalized;

    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, "0");
    const minutes = String(parsed.getMinutes()).padStart(2, "0");
    const hasTime = /[T\s]\d{2}:\d{2}/.test(normalized);
    const date = language === "en" ? `${month}/${day}/${year}` : `${day}/${month}/${year}`;
    return hasTime ? `${date} ${hours}:${minutes}` : date;
  }).join(", ");
}

function getWeightUnit(product) {
  if (!product?.pricePerWeight) return "";
  return product.typeOfWeight === "HECTOGRAM" ? "hg" : "kg";
}

export function buildQuoteEmailHtml({
  clientName,
  message,
  eventDate,
  validUntil,
  items,
  total,
  logoSrc = "",
  brandLabel = "Ristorante",
  language = "it",
  templateKind = "quote",
  tenantType = "",
  roomDetails = [],
}) {
  const lang = normalizeEmailLanguage(language);
  const text = EMAIL_COPY[lang];
  const isConferenceTenant = String(tenantType || "").toUpperCase() === "SALE_CONGRESSI";
  const isConfirmation = templateKind === "confirmation";
  const emailTitle = isConfirmation ? text.confirmationTitle : text.title;
  const defaultMessage = isConfirmation ? text.confirmationDefaultMessage : text.defaultMessage;
  const contactLine = isConfirmation ? text.confirmationContactLine : text.contactLine;
  const assistanceLine = isConfirmation ? text.confirmationAssistanceLine : text.assistanceLine;
  const footer = isConfirmation ? text.confirmationFooter : text.footer;
  const formattedEventDate = formatEmailDate(eventDate, lang);
  const formattedValidUntil = formatEmailDate(validUntil, lang);
  const logoBlock = logoSrc ? `
          <td align="right" style="vertical-align:middle;padding-left:20px;">
            <img src="${escapeHtml(logoSrc)}" alt="Logo" style="display:block;max-width:120px;max-height:72px;object-fit:contain;border:0;"/>
          </td>` : "";

  const itemRows = items.map((item, i) => {
    const quantityCell = isConferenceTenant
      ? escapeHtml(item.qty)
      : `${escapeHtml(item.qty)} ${escapeHtml(item.unit)}`;
    return `
    <tr style="background:${i % 2 === 0 ? "#f8fafc" : "#ffffff"};">
      <td style="padding:10px 14px;font-size:13px;color:#2d3748;border-bottom:1px solid #edf2f7;">
        <div>${escapeHtml(item.name)}</div>
        ${item.note ? `<div style="margin-top:4px;font-size:11px;color:#718096;">Nota: ${escapeHtml(item.note)}</div>` : ""}
      </td>
      <td align="right" style="padding:10px 14px;font-size:13px;color:#718096;border-bottom:1px solid #edf2f7;white-space:nowrap;">${quantityCell}</td>
      <td align="right" style="padding:10px 14px;font-size:13px;color:#718096;border-bottom:1px solid #edf2f7;white-space:nowrap;">€ ${escapeHtml(item.unitPrice)}</td>
      <td align="right" style="padding:10px 14px;font-size:13px;color:#2d3748;font-weight:600;border-bottom:1px solid #edf2f7;white-space:nowrap;">€ ${escapeHtml(item.total)}</td>
    </tr>
  `;
  }).join("");

  const eventDateBlock = formattedEventDate ? `
    <tr>
      <td style="padding:0 40px 20px;">
        <table cellpadding="0" cellspacing="0" width="100%" style="background:#eef2ff;border-radius:6px;padding:12px 16px;">
          <tr>
            <td style="font-size:13px;color:#3d4a6b;">&#128197; &nbsp;<strong>${escapeHtml(text.eventDate)}:</strong> ${escapeHtml(formattedEventDate)}</td>
          </tr>
        </table>
      </td>
    </tr>` : "";

  const validUntilBlock = formattedValidUntil ? `
    <tr>
      <td style="padding:0 40px 24px;">
        <p style="margin:0;font-size:12px;color:#a0aec0;font-style:italic;">
          &#9203; ${escapeHtml(text.validUntil)}: <strong style="color:#718096;">${escapeHtml(formattedValidUntil)}</strong>
        </p>
      </td>
    </tr>` : "";

  const roomsBlock = isConferenceTenant && roomDetails.length > 0 ? `
    <tr>
      <td style="padding:0 40px 20px;">
        <table cellpadding="0" cellspacing="0" width="100%" style="background:#f7f9fc;border-radius:6px;padding:12px 16px;border:1px solid #e2e8f0;">
          <tr><td style="font-size:13px;color:#1a1a2e;font-weight:700;padding-bottom:8px;">${escapeHtml(text.rooms)}</td></tr>
          ${roomDetails.map((room) => `
            <tr><td style="font-size:13px;color:#4a5568;padding:4px 0;">
              <strong>${escapeHtml(room.roomName)}</strong>
              &mdash; ${escapeHtml(text.usageType)}: ${escapeHtml(room.usageTypeName || "-")}
              &mdash; ${escapeHtml(text.setup)}: ${escapeHtml(room.setupName || "-")}
            </td></tr>
          `).join("")}
        </table>
      </td>
    </tr>` : "";

  return `<!DOCTYPE html>
<html lang="${lang}">
<head><meta charset="UTF-8"/><meta name="viewport" content="width=device-width, initial-scale=1.0"/><title>${escapeHtml(emailTitle)}</title></head>
<body style="margin:0;padding:0;background:#f0f0f0;font-family:Arial,Helvetica,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f0f0f0;padding:40px 0;">
  <tr><td align="center">
    <table width="620" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.10);">
      <tr>
        <td style="background:#1a1a2e;padding:32px 40px;">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td style="vertical-align:middle;">
                <h1 style="margin:0;color:#ffffff;font-size:22px;font-weight:700;letter-spacing:0.5px;">${escapeHtml(emailTitle)}</h1>
                <p style="margin:6px 0 0;color:#9eaabf;font-size:13px;">${escapeHtml(brandLabel)}</p>
              </td>
              ${logoBlock}
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td style="padding:32px 40px 20px;">
          <p style="margin:0 0 10px;font-size:15px;color:#1a1a2e;">${escapeHtml(text.greeting)} <strong>${escapeHtml(clientName)}</strong>,</p>
          <p style="margin:0;font-size:14px;color:#555555;line-height:1.75;">${escapeHtml(message || defaultMessage)}</p>
        </td>
      </tr>
      ${eventDateBlock}
      ${roomsBlock}
      <tr>
        <td style="padding:0 40px 24px;">
          <table width="100%" cellpadding="0" cellspacing="0" style="border-radius:6px;overflow:hidden;border:1px solid #e2e8f0;">
            <tr style="background:#1a1a2e;">
              <th align="left"  style="padding:11px 14px;font-size:11px;font-weight:600;color:#ffffff;text-transform:uppercase;letter-spacing:0.6px;">${escapeHtml(text.service)}</th>
              <th align="right" style="padding:11px 14px;font-size:11px;font-weight:600;color:#ffffff;text-transform:uppercase;letter-spacing:0.6px;white-space:nowrap;">${escapeHtml(isConferenceTenant ? text.quantity : text.quantityUnit)}</th>
              <th align="right" style="padding:11px 14px;font-size:11px;font-weight:600;color:#ffffff;text-transform:uppercase;letter-spacing:0.6px;white-space:nowrap;">${escapeHtml(text.unitPrice)}</th>
              <th align="right" style="padding:11px 14px;font-size:11px;font-weight:600;color:#ffffff;text-transform:uppercase;letter-spacing:0.6px;">${escapeHtml(text.total)}</th>
            </tr>
            ${itemRows}
            <tr style="background:#1a1a2e;">
              <td colspan="3" align="right" style="padding:14px 14px;font-size:13px;font-weight:700;color:#ffffff;">${escapeHtml(text.overallTotal)}</td>
              <td align="right" style="padding:14px 14px;font-size:16px;font-weight:700;color:#4fc3f7;white-space:nowrap;">€ ${escapeHtml(total)}</td>
            </tr>
          </table>
        </td>
      </tr>
      ${validUntilBlock}
      <tr>
        <td style="padding:0 40px 32px;">
          <table cellpadding="0" cellspacing="0" width="100%" style="background:#f7f9fc;border-left:4px solid #1a1a2e;border-radius:0 6px 6px 0;padding:16px 20px;">
            <tr>
              <td style="font-size:13px;color:#4a5568;line-height:1.75;">
                ${escapeHtml(contactLine)}<br/>
                ${escapeHtml(assistanceLine)}
              </td>
            </tr>
          </table>
        </td>
      </tr>
      <tr>
        <td style="background:#f7fafc;padding:20px 40px;border-top:1px solid #e2e8f0;">
          <p style="margin:0;font-size:11px;color:#a0aec0;text-align:center;line-height:1.7;">
            ${escapeHtml(footer(brandLabel))}
          </p>
        </td>
      </tr>
    </table>
  </td></tr>
</table>
</body>
</html>`;
}

function normalizeCatalog(products, packagesList, t) {
  const serviceEntries = (Array.isArray(products) ? products : []).map((p) => ({
    key: `product_${p.id}`,
    sourceType: "service",
    id: p.id,
    name: p.name,
    unit: getWeightUnit(p) ? `Euro/${getWeightUnit(p)}` : t("quoteAdmin.unitEach"),
    priceUnitLabel: getWeightUnit(p) ? `Euro/${getWeightUnit(p)}` : "EUR",
    unitPrice: Number(p.price || 0),
    subtitle: [p.category?.name, p.subcategory?.name].filter(Boolean).join(" - "),
  }));

  const packageEntries = (Array.isArray(packagesList) ? packagesList : []).map((p) => ({
    key: `package_${p.id}`,
    sourceType: "package",
    id: p.id,
    name: p.name,
    unit: t("quoteAdmin.packageUnit"),
    priceUnitLabel: "EUR",
    unitPrice: Number(p.price || 0),
    subtitle: t("quoteAdmin.packageSubtitle", { count: Array.isArray(p.items) ? p.items.length : 0 }),
  }));

  return [...packageEntries, ...serviceEntries];
}

function buildIsoRangeFromDate(dateString) {
  const base = dateString ? new Date(dateString) : new Date();
  const normalized = Number.isNaN(base.getTime()) ? new Date() : base;
  const start = new Date(normalized);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

export default function Preventivi({ embedded = false, onAfterSendQuote }) {
  const { t, i18n } = useTranslation();
  const qa = useCallback((key, options) => t(`quoteAdmin.${key}`, options), [t]);

  const STEPS = [
    t("preventivi.step_client"),
    t("preventivi.step_services"),
    t("preventivi.step_preview"),
  ];

  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const emailLanguage = normalizeEmailLanguage(i18n.language);
  const emailBrandLabel = tenantType === "SALE_CONGRESSI" ? "Sale Congressi" : "Ristorante";

  const [activeStep, setActiveStep] = useState(0);
  const [recipientMode, setRecipientMode] = useState("existing_client");
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogTypeFilter, setCatalogTypeFilter] = useState("all");
  const [catalogEntries, setCatalogEntries] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]);
  const [guestCount, setGuestCount] = useState("");
  const [items, setItems] = useState([]);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customServiceName, setCustomServiceName] = useState("");
  const [customServiceUnit, setCustomServiceUnit] = useState("");
  const [customServicePrice, setCustomServicePrice] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState("");
  const [eventSyncMessage, setEventSyncMessage] = useState("");
  const [eventSyncSeverity, setEventSyncSeverity] = useState("info");
  const [tenantLogo, setTenantLogo] = useState({ base64: "", fileName: "" });
  const [includeLogo, setIncludeLogo] = useState(false);

  useEffect(() => {
    async function loadRecipients() {
      try {
        const [clientsData, companiesData] = await Promise.all([
          getAllClienti(token, tenant),
          getAllCompanies(token, tenant),
        ]);
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setCompanies(Array.isArray(companiesData) ? companiesData : []);
      } catch (err) {
        console.error("Errore caricamento destinatari:", err);
      }
    }
    loadRecipients();
  }, [token, tenant]);

  useEffect(() => {
    async function loadSettings() {
      try {
        const settings = await getTenantSettings(token, tenant);
        setTenantLogo({
          base64: settings?.logoBase64 || settings?.tenantLogoBase64 || settings?.logo || "",
          fileName: settings?.logoFileName || settings?.tenantLogoFileName || "",
        });
      } catch (err) {
        console.error("Errore caricamento impostazioni preventivo:", err);
      }
    }
    loadSettings();
  }, [token, tenant]);

  useEffect(() => {
    if (!tenantLogo.base64 && includeLogo) setIncludeLogo(false);
  }, [tenantLogo.base64, includeLogo]);

  useEffect(() => {
    async function loadCatalog() {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const [products, packagesList, tablesData] = await Promise.all([
          getAllProdotti(token, tenant),
          getAllPackages(token, tenant),
          getAllTavoli(token, tenant),
        ]);
        setCatalogEntries(normalizeCatalog(products, packagesList, t));
        setTables(Array.isArray(tablesData) ? tablesData : []);
      } catch (err) {
        setCatalogError(italianErrorMessage(err, qa("catalogLoadError")));
      } finally {
        setCatalogLoading(false);
      }
    }
    loadCatalog();
  }, [token, tenant, t, qa]);

  const recipientEmail = useMemo(() => {
    if (recipientMode === "existing_client") return selectedClient?.email || "";
    if (recipientMode === "existing_company") return selectedCompany?.email || "";
    return manualEmail;
  }, [recipientMode, selectedClient, selectedCompany, manualEmail]);

  const recipientName = useMemo(() => {
    if (recipientMode === "existing_client") {
      return `${selectedClient?.name || ""} ${selectedClient?.surname || ""}`.trim();
    }
    if (recipientMode === "existing_company") return selectedCompany?.companyName || "";
    return manualName;
  }, [recipientMode, selectedClient, selectedCompany, manualName]);

  const recipientEmailValid = recipientEmail.includes("@");

  const step1Valid = useMemo(() => {
    if (recipientMode === "existing_client") return Boolean(selectedClient && recipientEmailValid);
    if (recipientMode === "existing_company") return Boolean(selectedCompany && recipientEmailValid);
    return manualName.trim().length > 0 && recipientEmailValid;
  }, [recipientMode, selectedClient, selectedCompany, manualName, recipientEmailValid]);

  const parsedGuestCount = useMemo(() => {
    const n = Number.parseInt(guestCount, 10);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [guestCount]);

  const availableTables = useMemo(() => {
    if (!parsedGuestCount) return tables;
    return tables.filter((table) => Number(table?.maxSeats || 0) >= parsedGuestCount);
  }, [tables, parsedGuestCount]);

  const step2Valid = items.length > 0 && Boolean(parsedGuestCount);
  const grandTotal = items.reduce((sum, i) => sum + Number(i.qty || 0) * Number(i.unitPrice || 0), 0);
  const emailItems = useMemo(() => items.map((i) => ({
    name: i.name,
    unit: i.unit,
    qty: Number(i.qty || 0),
    unitPrice: money(i.unitPrice),
    total: money(Number(i.qty || 0) * Number(i.unitPrice || 0)),
  })), [items]);
  const emailPreviewHtml = useMemo(() => buildQuoteEmailHtml({
    clientName: recipientName || t("client"),
    message,
    eventDate,
    items: emailItems,
    total: money(grandTotal),
    logoSrc: includeLogo ? tenantLogo.base64 : "",
    brandLabel: emailBrandLabel,
    language: emailLanguage,
    tenantType,
  }), [recipientName, t, message, eventDate, emailItems, grandTotal, includeLogo, tenantLogo.base64, emailBrandLabel, emailLanguage, tenantType]);

  const visibleCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return catalogEntries.filter((entry) => {
      const byType = catalogTypeFilter === "all" || entry.sourceType === catalogTypeFilter;
      if (!byType) return false;
      if (!q) return true;
      return `${entry.name} ${entry.subtitle || ""}`.toLowerCase().includes(q);
    });
  }, [catalogEntries, catalogSearch, catalogTypeFilter]);

  const addFromCatalog = (entry) => {
    if (items.find((i) => i.key === entry.key)) return;
    setItems((prev) => [
      ...prev,
      {
        key: entry.key,
        id: entry.id,
        sourceType: entry.sourceType,
        name: entry.name,
        unit: entry.unit,
        priceUnitLabel: entry.priceUnitLabel || "EUR",
        unitPrice: Number(entry.unitPrice || 0),
        qty: 1,
      },
    ]);
  };

  const addCustomService = () => {
    if (!customServiceName.trim() || !customServicePrice) return;
    setItems((prev) => [
      ...prev,
      {
        key: `custom_${Date.now()}`,
        id: null,
        sourceType: "custom",
        name: customServiceName.trim(),
        unit: customServiceUnit.trim() || qa("unitEach"),
        priceUnitLabel: "EUR",
        unitPrice: Number(customServicePrice || 0),
        qty: 1,
      },
    ]);
    setCustomServiceName("");
    setCustomServiceUnit("");
    setCustomServicePrice("");
    setShowAddCustom(false);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateItem = (key, field, value) => {
    setItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      if (field === "qty" || field === "unitPrice") {
        return { ...i, [field]: Number(value || 0) };
      }
      return { ...i, [field]: value };
    }));
  };

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    setSendError("");
    setEventSyncMessage("");
    let emailSent = false;
    let emailErrorMessage = "";
    let eventCreated = false;
    let eventErrorMessage = "";

    try {
      await inviaPreventivoEmail({
        to: recipientEmail,
        clientName: recipientName,
        subject: subject || `${t("preventivi.page")} - ${recipientName}`,
        message,
        eventDate,
        includeLogo,
        language: emailLanguage,
        items: emailItems,
        total: money(grandTotal),
      }, token, tenant);
      emailSent = true;
    } catch (err) {
      emailSent = false;
      emailErrorMessage = italianErrorMessage(err, qa("sendQuoteError"));
    }

    try {
      const canCreateEvent = (
        (recipientMode === "existing_client" && Number.isInteger(selectedClient?.id))
        || (recipientMode === "existing_company" && Number.isInteger(selectedCompany?.id))
      );
      if (!canCreateEvent) {
        throw new Error(qa("eventCreateRequired"));
      }

        const { startIso, endIso } = buildIsoRangeFromDate(eventDate);

        const packageItems = items
          .filter((i) => i.sourceType === "package" && Number.isInteger(i.id) && i.id > 0)
          .map((i) => ({
            packageId: Number(i.id),
            quantity: Number(i.qty || 1),
            unitPrice: Number(i.unitPrice || 0),
          }));

        const serviceItems = items
          .filter((i) => i.sourceType === "service" && Number.isInteger(i.id) && i.id > 0)
          .map((i) => ({
            productId: Number(i.id),
            quantity: Number(i.qty || 1),
            unitPrice: Number(i.unitPrice || 0),
          }));

        const customItems = items.filter((i) => i.sourceType === "custom");
        const customItemsNote = customItems.length > 0
          ? `\n${qa("customServicesNote")}\n- ${customItems
            .map((i) => `${i.name} (${i.qty} ${i.unit}) EUR ${money(i.unitPrice)}`)
            .join("\n- ")}`
          : "";

        const payload = {
          eventName: (subject || qa("quoteEventName", { name: recipientName || qa("recipientFallback") })).trim(),
          status: "PREVENTIVO",
          note: `${message || ""}${customItemsNote}`.trim() || undefined,
          ...(recipientMode === "existing_client" ? { clientId: selectedClient?.id } : {}),
          ...(recipientMode === "existing_company" ? { companyId: selectedCompany?.id } : {}),
          days: [
            {
              dayDate: startIso,
              startDate: startIso,
              endDate: endIso,
              guests: parsedGuestCount || 1,
              tableIds: selectedTables
                .map((table) => Number(table?.id))
                .filter((id) => Number.isInteger(id) && id > 0),
              packageItems,
              serviceItems,
              note: message || undefined,
            },
          ],
        };

        await createReservationEvent(payload, token, tenant);
        eventCreated = true;
        if (typeof onAfterSendQuote === "function") {
          await onAfterSendQuote();
        }
    } catch (err) {
      eventCreated = false;
      eventErrorMessage = italianErrorMessage(err, qa("eventCreateError"));
    }

    if (eventCreated) {
      if (emailSent) {
        setEventSyncSeverity("success");
        setEventSyncMessage(qa("sentAndEventCreated"));
        setSendResult("success");
      } else {
        setEventSyncSeverity("warning");
        setEventSyncMessage(qa("eventCreatedEmailFailed", { error: emailErrorMessage }));
        setSendError(qa("emailNotSent", { error: emailErrorMessage }));
        setSendResult("error");
      }
    } else {
      const errors = [];
      if (!emailSent && emailErrorMessage) errors.push(qa("emailNotSent", { error: emailErrorMessage }));
      if (eventErrorMessage) errors.push(qa("eventNotCreated", { error: eventErrorMessage }));
      setSendError(errors.join(" | ") || qa("operationNotCompleted"));
      setSendResult("error");
    }
    setSending(false);
  };

  const handleReset = () => {
    setActiveStep(0);
    setRecipientMode("existing_client");
    setSelectedClient(null);
    setSelectedCompany(null);
    setSelectedTables([]);
    setGuestCount("");
    setManualName("");
    setManualEmail("");
    setItems([]);
    setSubject("");
    setMessage("");
    setEventDate("");
    setIncludeLogo(false);
    setSendResult(null);
    setSendError("");
    setEventSyncMessage("");
  };

  const content = (
    <Box sx={{
      mx: embedded ? 0 : { xs: 2, md: 4 },
      mt: embedded ? 0 : 2,
      mb: embedded ? 0 : 6,
    }}>
        <Typography variant="h4" gutterBottom>{t("preventivi.page")}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("preventivi.subtitle")}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>{t("preventivi.select_recipient")}</Typography>

              <ToggleButtonGroup
                value={recipientMode}
                exclusive
                size="small"
                onChange={(_, value) => value && setRecipientMode(value)}
                sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}
              >
                <ToggleButton value="existing_client">
                  <PersonIcon sx={{ mr: 1, fontSize: 18 }} /> {qa("client")}
                </ToggleButton>
                <ToggleButton value="existing_company">
                  <BusinessIcon sx={{ mr: 1, fontSize: 18 }} /> {qa("company")}
                </ToggleButton>
                <ToggleButton value="manual">
                  <EmailIcon sx={{ mr: 1, fontSize: 18 }} /> {qa("manualEmail")}
                </ToggleButton>
              </ToggleButtonGroup>

              {recipientMode === "existing_client" && (
                <Autocomplete
                  options={clients}
                  getOptionLabel={(c) => `${c.name} ${c.surname} - ${c.email || qa("noEmail")}`}
                  value={selectedClient}
                  onChange={(_, v) => setSelectedClient(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("client")}
                      placeholder={t("preventivi.search_client")}
                      size="small"
                    />
                  )}
                  sx={{ maxWidth: 520 }}
                />
              )}

              {recipientMode === "existing_company" && (
                <Autocomplete
                  options={companies}
                  getOptionLabel={(c) => `${c.companyName} - ${c.email || qa("noEmail")}`}
                  value={selectedCompany}
                  onChange={(_, v) => setSelectedCompany(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={qa("company")}
                      placeholder={qa("searchCompany")}
                      size="small"
                    />
                  )}
                  sx={{ maxWidth: 520 }}
                />
              )}

              {recipientMode === "manual" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 520 }}>
                  <TextField
                    label={t("preventivi.recipient_name")}
                    size="small"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                  />
                  <TextField
                    label={t("preventivi.email_address")}
                    size="small"
                    type="email"
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                  />
                </Box>
              )}

              {((recipientMode === "existing_company" && selectedCompany && !recipientEmailValid)
                || (recipientMode === "existing_client" && selectedClient && !recipientEmailValid)) && (
                <Alert severity="warning" sx={{ mt: 2, maxWidth: 520 }}>
                  {qa("noEmailWarning")}
                </Alert>
              )}

              {step1Valid && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1, maxWidth: 520 }}>
                  <Typography variant="body2">
                    <strong>{t("preventivi.to")}:</strong> {recipientName} &lt;{recipientEmail}&gt;
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {activeStep === 1 && (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>{qa("realCatalog")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {qa("realCatalogSubtitle")}
                </Typography>

                <TextField
                  size="small"
                  type="number"
                  label={qa("guestCount")}
                  value={guestCount}
                  onChange={(e) => {
                    setGuestCount(e.target.value);
                    setSelectedTables([]);
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ maxWidth: 260, mb: 2 }}
                />

                <Autocomplete
                  multiple
                  options={availableTables}
                  value={selectedTables}
                  getOptionLabel={(table) => {
                    const tableName = table?.name || `${qa("table")} ${table?.id || ""}`.trim();
                    const roomName = table?.room?.name || "";
                    const seats = Number(table?.maxSeats || 0);
                    const label = roomName ? `${roomName} - ${tableName}` : tableName;
                    return `${label} (${seats} ${qa("seats")})`;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, value) => setSelectedTables(Array.isArray(value) ? value : [])}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label={qa("tablesOptional", { defaultValue: "Tavoli (opzionali nel preventivo)" })}
                      placeholder={qa("selectTables")}
                      helperText={`${
                        parsedGuestCount
                          ? qa("compatibleTables", { count: availableTables.length, guests: parsedGuestCount })
                          : qa("insertGuestsFirst")
                      } ${qa("tablesRequiredAtConfirmation", {
                        defaultValue: "L'assegnazione diventa obbligatoria quando il preventivo viene convertito o confermato.",
                      })}`}
                    />
                  )}
                  sx={{ maxWidth: 720, mb: 2 }}
                />

                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
                  <TextField
                    size="small"
                    label={qa("search")}
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    sx={{ minWidth: { xs: 0, sm: 280 }, flex: 1, width: { xs: "100%", sm: "auto" } }}
                  />
                  <TextField
                    size="small"
                    select
                    label={qa("type")}
                    value={catalogTypeFilter}
                    onChange={(e) => setCatalogTypeFilter(e.target.value)}
                    sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}
                  >
                    <MenuItem value="all">{qa("all")}</MenuItem>
                    <MenuItem value="package">{qa("packages")}</MenuItem>
                    <MenuItem value="service">{qa("servicesSingle")}</MenuItem>
                  </TextField>
                  <Typography variant="body2" color="text.secondary">
                    {visibleCatalog.length} {qa("results")}
                  </Typography>
                </Box>

                {catalogLoading ? (
                  <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">{qa("catalogLoading")}</Typography>
                  </Box>
                ) : catalogError ? (
                  <Alert severity="error">{catalogError}</Alert>
                ) : (
                  <Paper variant="outlined">
                    <TableContainer sx={{ maxHeight: 360 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 130 }}><strong>{qa("type")}</strong></TableCell>
                            <TableCell><strong>{qa("name")}</strong></TableCell>
                            <TableCell><strong>{qa("detail")}</strong></TableCell>
                            <TableCell align="right" sx={{ width: 120 }}><strong>{qa("price")}</strong></TableCell>
                            <TableCell align="right" sx={{ width: 120 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleCatalog.map((entry) => {
                            const alreadyAdded = items.some((i) => i.key === entry.key);
                            return (
                              <TableRow
                                key={entry.key}
                                hover
                                selected={alreadyAdded}
                                sx={{ opacity: alreadyAdded ? 0.72 : 1 }}
                              >
                                <TableCell>
                                  {entry.sourceType === "package" ? qa("package") : qa("service")}
                                </TableCell>
                                <TableCell>{entry.name}</TableCell>
                                <TableCell sx={{ color: "text.secondary" }}>
                                  {entry.subtitle || "-"}
                                </TableCell>
                                <TableCell align="right">{entry.priceUnitLabel || "EUR"} {money(entry.unitPrice)}</TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant={alreadyAdded ? "outlined" : "contained"}
                                    onClick={() => addFromCatalog(entry)}
                                    disabled={alreadyAdded}
                                  >
                                    {alreadyAdded ? qa("added") : qa("add")}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {visibleCatalog.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5}>{qa("noResults")}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>{t("preventivi.quote_items")}</Typography>

                {items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("preventivi.no_services_selected")}
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell><strong>{t("preventivi.col_service")}</strong></TableCell>
                          <TableCell><strong>{t("preventivi.col_unit")}</strong></TableCell>
                          <TableCell align="right"><strong>{t("preventivi.col_qty")}</strong></TableCell>
                          <TableCell align="right"><strong>{t("preventivi.col_unit_price")}</strong></TableCell>
                          <TableCell align="right"><strong>{t("preventivi.col_total")}</strong></TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.key}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>{item.unit}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                variant="standard"
                                value={item.qty}
                                onChange={(e) => updateItem(item.key, "qty", e.target.value)}
                                inputProps={{ min: 1, style: { width: 50, textAlign: "right" } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                variant="standard"
                                value={item.unitPrice}
                                onChange={(e) => updateItem(item.key, "unitPrice", e.target.value)}
                                inputProps={{ min: 0, style: { width: 80, textAlign: "right" } }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">{item.priceUnitLabel || "EUR"}</InputAdornment>,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <strong>EUR {money(Number(item.qty) * Number(item.unitPrice))}</strong>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" color="error" onClick={() => removeItem(item.key)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: "action.selected" }}>
                          <TableCell colSpan={4} align="right">
                            <strong>{t("preventivi.grand_total")}:</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary.main">
                              EUR {money(grandTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Box sx={{ mt: 2 }}>
                  {!showAddCustom ? (
                    <Button startIcon={<AddIcon />} size="small" onClick={() => setShowAddCustom(true)}>
                      {t("preventivi.add_custom")}
                    </Button>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end", mt: 1 }}>
                      <TextField
                        label={t("preventivi.custom_name")}
                        size="small"
                        value={customServiceName}
                        onChange={(e) => setCustomServiceName(e.target.value)}
                        sx={{ flex: 2, minWidth: 180 }}
                      />
                      <TextField
                        label={t("preventivi.custom_unit")}
                        size="small"
                        value={customServiceUnit}
                        onChange={(e) => setCustomServiceUnit(e.target.value)}
                        sx={{ flex: 1, minWidth: 100 }}
                      />
                      <TextField
                        label={t("preventivi.custom_price")}
                        size="small"
                        type="number"
                        value={customServicePrice}
                        onChange={(e) => setCustomServicePrice(e.target.value)}
                        sx={{ flex: 1, minWidth: 120 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">EUR</InputAdornment> }}
                      />
                      <Button variant="contained" size="small" onClick={addCustomService}>
                        {t("preventivi.add")}
                      </Button>
                      <Button size="small" onClick={() => setShowAddCustom(false)}>
                        {t("cancel")}
                      </Button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            {sendResult === "success" ? (
              <Card>
                <CardContent sx={{ p: 5, textAlign: "center" }}>
                  <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
                  <Typography variant="h5" gutterBottom>{t("preventivi.sent_title")}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {t("preventivi.sent_desc")} <strong>{recipientEmail}</strong>.
                  </Typography>
                  <Button variant="contained" onClick={handleReset}>
                    {t("preventivi.new_quote")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>{t("preventivi.email_details")}</Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          label={t("preventivi.to")}
                          size="small"
                          disabled
                          fullWidth
                          value={`${recipientName} <${recipientEmail}>`}
                        />
                        <TextField
                          label={t("preventivi.subject")}
                          size="small"
                          fullWidth
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          placeholder={`${t("preventivi.page")} - ${recipientName}`}
                        />
                        <DateTimeLocal24Field
                          label={qa("eventDateTime")}
                          size="small"
                          value={eventDate}
                          onChange={setEventDate}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={includeLogo}
                              disabled={!tenantLogo.base64}
                              onChange={(e) => setIncludeLogo(e.target.checked)}
                            />
                          }
                          label={t("preventivi.include_logo", { defaultValue: "Includi logo nell'e-mail" })}
                        />
                        <TextField
                          label={t("preventivi.intro_message")}
                          multiline
                          rows={4}
                          size="small"
                          fullWidth
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder={t("preventivi.intro_placeholder")}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ flex: 1.2 }}>
                  <Card>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>{t("preventivi.email_preview")}</Typography>
                      <Box
                        component="iframe"
                        title={t("preventivi.email_preview")}
                        srcDoc={emailPreviewHtml}
                        sx={{
                          width: "100%",
                          height: { xs: 620, md: 760 },
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          bgcolor: "#f0f0f0",
                        }}
                      />
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}

            {sendResult === "error" && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {t("preventivi.send_error_label")}: {sendError}
              </Alert>
            )}
          </Box>
        )}

        {sendResult !== "success" && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={() => setActiveStep((s) => s - 1)}
              startIcon={<ArrowBackIcon />}
            >
              {t("preventivi.back")}
            </Button>

            {activeStep < STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setActiveStep((s) => s + 1)}
                disabled={(activeStep === 0 && !step1Valid) || (activeStep === 1 && !step2Valid)}
                endIcon={<ArrowForwardIcon />}
              >
                {t("preventivi.next")}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={handleSend}
                disabled={sending || !step1Valid || !step2Valid}
                startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              >
                {sending ? t("preventivi.sending") : t("preventivi.send")}
              </Button>
            )}
          </Box>
        )}

        {eventSyncMessage && sendResult === "success" && (
          <Alert severity={eventSyncSeverity} sx={{ mt: 2 }}>
            {eventSyncMessage}
          </Alert>
        )}
      </Box>
  );

  if (embedded) return content;
  return <SidebarLayout className="admin-responsive-page">{content}</SidebarLayout>;
}
